import {
  readEffectiveInventorizationContext,
  readEffectiveInventorizationIdentity,
  readEffectiveInventorizationRegisters,
  readEffectiveInventorizationTechnicalDetails,
} from '@/features/inventorization/domain/effective-inventorization-values';
import { readInventorizationContext } from '@/features/inventorization/domain/inventorization-context';
import { validateInventorizationContext } from '@/features/inventorization/domain/inventorization-context-validation';
import { readInventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity';
import { validateInventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity-validation';
import { readInventorizationRegisters } from '@/features/inventorization/domain/inventorization-reading';
import { validateInventorizationRegisterReadings } from '@/features/inventorization/domain/inventorization-reading-validation';
import { readInventorizationTechnicalDetails } from '@/features/inventorization/domain/inventorization-technical';
import { validateInventorizationTechnicalDetails } from '@/features/inventorization/domain/inventorization-technical-validation';
import type { CaptureSession, ValidationIssue } from '@/shared/capture/capture-session';

/** Drops "missing value" issues for values filled from photo extraction, which is what sync submits. */
export function dropIssuesResolvedByExtraction(session: CaptureSession): CaptureSession {
  const resolvedCodes = new Set([
    ...readResolvedCodes(
      validateInventorizationIdentity(readInventorizationIdentity(session)),
      validateInventorizationIdentity(readEffectiveInventorizationIdentity(session)),
    ),
    ...readResolvedCodes(
      validateInventorizationContext(readInventorizationContext(session)),
      validateInventorizationContext(readEffectiveInventorizationContext(session)),
    ),
    ...readResolvedCodes(
      validateInventorizationTechnicalDetails(readInventorizationTechnicalDetails(session)),
      validateInventorizationTechnicalDetails(readEffectiveInventorizationTechnicalDetails(session)),
    ),
    ...readResolvedCodes(
      validateInventorizationRegisterReadings(session, readInventorizationRegisters(session)),
      validateInventorizationRegisterReadings(session, readEffectiveInventorizationRegisters(session)),
    ),
  ]);

  if (!session.validationErrors.some(issue => resolvedCodes.has(issue.code))) {
    return session;
  }

  return {
    ...session,
    validationErrors: session.validationErrors.filter(issue => !resolvedCodes.has(issue.code)),
  };
}

function readResolvedCodes(rawIssues: ValidationIssue[], effectiveIssues: ValidationIssue[]) {
  const effectiveCodes = new Set(effectiveIssues.map(issue => issue.code));

  return rawIssues.map(issue => issue.code).filter(code => !effectiveCodes.has(code));
}
